import z from 'zod/v3';

export const CreditCardSchema = z.object({
  kind: z.string(),
  lastFour: z.string(),
  expiration: z.string(),
});

export const CreditCardInfoSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('saved-card'),
    savedCard: CreditCardSchema,
  }),
  z.object({
    kind: z.literal('no-saved-card'),
  }),
]);

export const TopUpConfigSchema = z
  .object({
    amount: z.number(),
    threshold: z.number(),
  })
  .nullable();

export const OrbInfoSchema = z.object({
  creditsBalance: z.number(), // TODO: rename to dollar balance
  invoicesPortalUrl: z.string(),
  creditCardInfo: CreditCardInfoSchema,
  topUpConfig: TopUpConfigSchema,
});

const OrbEnterpriseInfoSchema = z.object({
  balance: z.object({
    credits: z.number(), // TODO: rename to credits balance
    engineeringHours: z.number(),
  }),
  invoicesPortalUrl: z.string(),
});

export const OrbSubscriptionSchema = z.object({
  orbSubscriptionId: z.string(),
  planName: z.string(),
  planId: z.string(),
  createdAt: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  status: z.enum(['active', 'ended', 'upcoming']),
});

/**
 * There are three main kinds of billing settings:
 *
 * Legacy Enterprise
 *  - Enterprise customers that are not orb-metered. Their contracts/payments/usage are manually handled.
 *
 * Orb-Metered Enterprise
 *  - View "CREDIT" balance & engineering hours balance
 *  - View invoices
 *  - View subscription history
 *
 * Orb-Metered Pay-As-You Go
 *  - View remaining balance
 *  - View invoices
 *  - View/edit saved credit cards
 *  - Add credits
 *  - view/set up auto top ups
 *  - (no subscription history view)
 *
 * Orb-Metered Business Plan ($500/month)
 *  - View remaining balance
 *  - View invoices
 *  - View/edit saved credit cards
 *  - Add credits
 *  - view/set up auto top ups
 *  - View subscription history
 */
const BillingSettingsInfoSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('legacy-enterprise') }),
  z.object({
    kind: z.literal('orb-enterprise'),
    orbEnterpriseInfo: OrbEnterpriseInfoSchema,
    orbSubscriptionHistory: OrbSubscriptionSchema.array(),
  }),
  z.object({
    kind: z.literal('orb-business'),
    orbInfo: OrbInfoSchema,
    orbSubscriptionHistory: OrbSubscriptionSchema.array(),
  }),
  z.object({
    kind: z.literal('orb-pay-as-you-go'),
    orbInfo: OrbInfoSchema,
  }),
]);

export const EnterpriseCreditsBalance = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('positive'),
    credits: z.number(),
  }),
  z.object({
    kind: z.literal('negative'),
    creditsOverageAmount: z.number(),
    dollarsToBeCharged: z.number(),
  }),
]);

export const BalanceInfoSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('orb-paygo-business'),
    currentCreditBalance: z.number(),
    canAddCC: z.boolean(),
  }),
  z.object({
    kind: z.literal('orb-enterprise'),
    balance: z.object({
      creditsCredits: EnterpriseCreditsBalance,
      engineeringHours: z.number(),
    }),
  }),
]);
